import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild, computed, effect, signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponentV2 } from '../../../../shared/components/icon-component/icon.component';
import { EnvelopeTrackComponent } from './envelope-track.component';
import { InfoTipComponent } from './info-tip.component';
import { DEBUG_HELP, FINDING_HELP } from '../debug-help';
import {
  findingClass, initials, laneBarClass, laneDotClass, markClass, markLabel, markTitle, segTitle,
} from '../lane-style.util';
import { SpanPlayback } from '../span-playback';
import { TimelineZoom } from '../timeline-zoom';
import { SyncDebugState } from '../sync-debug-state';
import { findingLabel, type Finding } from '../sync-findings.util';
import {
  PlaybackMode, PlaybackSides, TalkStat, TimedTurn, TimelineLane, audioSourceFor,
  audioSrcCandidatesOf, buildTranscriptModel, mmss, playbackModeFor, rulerTicks, segmentAt,
} from '../transcript-timing.util';
import type { ReportRecording, SessionReport } from '../tracking-demo.models';

/**
 * Audio-synced transcript player — the report's Transcript tab when the call has audio.
 *
 * Per turn: start offset, avatar, speaker (AI right-accented, humans left), wall clock,
 * spoken length, and word-by-word karaoke while that turn plays (word times interpolated
 * across the turn window — see transcript-timing.util). Below: an editor-style dock — an
 * AI row and a Human row of who-spoke-when segments (one colour per human, overlap
 * visible as parallel bars; click to seek) whose row labels are the listen toggles — both
 * lit plays the conversation, one off solos the other by swapping to that side's
 * sample-locked file at the same position — plus transport (±10 s, play/pause) and speed.
 * Clicking a turn seeks to its start.
 *
 * Debug mode (the "Debug" toggle) adds the sync findings: a Transcript row of turn
 * windows, an Events row of the host's marks, gap cards inside the transcript where
 * speech went untranscribed, waveforms under the bars, span playback, and a
 * copy-to-ticket report. All the maths lives in sync-findings.util / sync-debug-view.util.
 */
@Component({
  selector: 'app-transcript-player',
  standalone: true,
  imports: [CommonModule, IconComponentV2, EnvelopeTrackComponent, InfoTipComponent],
  templateUrl: './transcript-player.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex min-h-0 flex-1 flex-col' },
})
export class TranscriptPlayerComponent {
  @ViewChild('audioEl') private audioEl?: ElementRef<HTMLAudioElement>;
  @ViewChild('scroller') private scroller?: ElementRef<HTMLElement>;

  @Input({ required: true }) set report(r: SessionReport) {
    const m = buildTranscriptModel(r);
    this.rec = r.recording ?? null;
    this.sessionId = r.session?.sessionId ?? '';
    this.dbg.load(r, m);
    // No stored wave (recorded before envelopes existed): have it built now, once.
    if (r.recording && !this.dbg.hasWaveform()) queueMicrotask(() => this.buildWaveform.emit());
    this.debug.set(false); // everyone gets the normal view; debug is opt-in per session
    this.spans.stop();
    this.mode.set('mixed');
    this.sides.set({ human: true, agent: true });
    this.resumeAt = null;
    this.rows.set(m.rows);
    this.srcCandidates.set(audioSrcCandidatesOf(r.recording));
    this.srcIdx.set(0);
    this.durationSec.set(m.durationSec);
    this.stats.set(m.stats);
    this.deadAir.set(m.deadAirSec);
    this.lanes.set(m.lanes);
    this.playhead.set(0);
    this.playing.set(false);
    this.probe = 'idle';
    this.pendingPlay = false;
    clearTimeout(this.probeTimer);
  }

  readonly rows = signal<TimedTurn[]>([]);
  /** Fallback chain — audio-mix, then rendered video, then raw video, then public
   *  url (see audioSrcCandidatesOf). A URL being here only means it was uploaded,
   *  not that it decodes, so onError advances srcIdx instead of trusting index 0. */
  readonly srcCandidates = signal<string[]>([]);
  readonly srcIdx = signal(0);
  readonly src = computed(() => this.srcCandidates()[this.srcIdx()] ?? null);
  readonly stats = signal<TalkStat[]>([]);
  readonly deadAir = signal(0);
  readonly lanes = signal<TimelineLane[]>([]);
  readonly agentLanes = computed(() => this.lanes().filter((l) => l.ai));
  readonly humanLanes = computed(() => this.lanes().filter((l) => !l.ai));
  /** Zoom for the tracks (labels stay fixed; tracks scroll together). */
  readonly zoomer = new TimelineZoom();
  /** Time ruler under the rows, finer when zoomed. */
  readonly ticks = computed(() => rulerTicks(this.durationSec(), this.zoomer.level()));
  private sessionId = '';
  readonly debug = signal(false);
  /** "Build waveform" (parent owns API + report); fired once per recording that has none. */
  @Output() readonly buildWaveform = new EventEmitter<void>();
  // Lazy accessors: `durationSec` is declared further down this class.
  readonly dbg = new SyncDebugState(() => this.rows(), () => this.durationSec(), () => this.debug());
  readonly spans = new SpanPlayback(() => this.audioEl?.nativeElement ?? null, (el) => this.doPlay(el));
  readonly findingLabel = findingLabel;
  /** ⓘ texts (debug-help.ts). */
  readonly help = DEBUG_HELP;
  readonly findingHelp = FINDING_HELP;
  readonly findingClass = findingClass;
  readonly markClass = markClass;
  readonly markLabel = markLabel;
  readonly markTitle = markTitle;
  /** Who holds each row at the playhead — the live "now speaking" chips. */
  readonly agentNow = computed(() => segmentAt(this.agentLanes(), this.playhead()));
  readonly humanNow = computed(() => segmentAt(this.humanLanes(), this.playhead()));
  // ── playback source: the conversation, or one side of it ──
  private rec: ReportRecording | null = null;
  readonly mode = signal<PlaybackMode>('mixed');
  /** The AI / Human toggles; the file being played follows from them. */
  readonly sides = signal<PlaybackSides>({ human: true, agent: true });
  /** Position + play state to restore once the swapped-in file has loaded. */
  private resumeAt: number | null = null;
  private resumePlay = false;
  readonly durationSec = signal(0);
  readonly playhead = signal(0);
  readonly playing = signal(false);
  readonly rate = signal(1);
  readonly speeds = [1, 1.5, 2];
  readonly mmss = mmss;
  /** Word-by-word karaoke, OFF for now: per-word times are interpolated (no STT word
   *  timing exists), and on real calls the estimate visibly drifts — the turn-level
   *  highlight is the one that's actually synced. Flip to re-enable the word view. */
  readonly wordKaraoke = false;

  /** Index of the turn being (or last) spoken at the playhead: last row started by now. */
  readonly activeIdx = computed(() => {
    const sec = this.playhead();
    let idx = -1;
    for (const [i, r] of this.rows().entries()) {
      if (r.start != null && r.start <= sec) idx = i;
    }
    return idx;
  });

  /** Index of the karaoke word within the active turn (-1 before its first word). */
  readonly wordIdx = computed(() => {
    const words = this.rows()[this.activeIdx()]?.words;
    if (!words) return -1;
    const sec = this.playhead();
    let idx = -1;
    for (const [i, w] of words.entries()) {
      if (w.at <= sec) idx = i;
    }
    return idx;
  });

  /** MediaRecorder-WebM duration probe (Infinity → real) — same trick as before, see
   *  the git history of session-report for the measured numbers. */
  private probe: 'idle' | 'probing' | 'done' = 'idle';
  private raf = 0;
  private probeTimer = 0;
  /** Sentinel seek used to force duration resolution — currentTime readings anywhere
   *  near this are probe artifacts, never a real playback position (a call is never
   *  ~3.1e94 years long). Gating on the value itself (not just the `probe` flag) means
   *  a stuck probe can't silently freeze playhead sync forever — see onDuration. */
  private static readonly PROBE_SENTINEL_SEC = 1e6;

  constructor() {
    // Keep the active turn centered as playback moves through the call.
    effect(() => {
      const idx = this.activeIdx();
      if (idx >= 0) {
        document.getElementById(`pt-turn-${idx}`)
          ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    });
  }

  // ── media events ──
  onMeta(ev: Event): void {
    const el = ev.target as HTMLAudioElement;
    el.playbackRate = this.rate();
    if (this.probe === 'idle' && !isFinite(el.duration)) {
      this.probe = 'probing';
      el.currentTime = 1e101;
      // Safety net: some browsers/streams never fire a finite `durationchange` after
      // this seek. Without a fallback the probe stays 'probing' forever, and both
      // onTime and the onPlay rAF loop below silently stop updating `playhead` —
      // audio keeps playing but the transcript highlight/karaoke freezes in place.
      clearTimeout(this.probeTimer);
      this.probeTimer = window.setTimeout(() => {
        if (this.probe !== 'probing') return;
        console.warn('[transcript-player] probe stuck — forcing done to unfreeze sync');
        this.resolveProbe(el);
      }, 2000);
    }
    if (this.probe !== 'probing') this.applyResume(el);
  }

  /** After a Source swap: the files share one clock, so the old position is the new one. */
  private applyResume(el: HTMLAudioElement): void {
    if (this.resumeAt === null) return;
    el.currentTime = this.resumeAt;
    this.playhead.set(el.currentTime);
    this.resumeAt = null;
    if (this.resumePlay) this.doPlay(el);
    this.resumePlay = false;
  }

  onDuration(ev: Event): void {
    const el = ev.target as HTMLAudioElement;
    if (isFinite(el.duration) && el.duration > 0) this.durationSec.set(el.duration);
    if (this.probe === 'probing' && isFinite(el.duration)) this.resolveProbe(el);
  }

  /** A source can be present (uploaded) without actually decoding — e.g. a beheaded
   *  webm mix. Rather than trust the first candidate blindly, fall through to the
   *  next one (video, then public url) the same way an <img> falls back on onerror. */
  onError(ev: Event): void {
    const el = ev.target as HTMLAudioElement;
    const idx = this.srcIdx();
    const candidates = this.srcCandidates();
    if (!candidates.length) return; // no src bound yet — not a real playback failure
    console.warn('[transcript-player] source failed to load, trying next candidate', {
      src: el.currentSrc, code: el.error?.code, message: el.error?.message,
      idx, of: candidates.length,
    });
    if (idx < candidates.length - 1) {
      clearTimeout(this.probeTimer);
      this.probe = 'idle';
      this.pendingPlay = false;
      this.playhead.set(0);
      this.srcIdx.set(idx + 1);
    }
  }

  /** Probe finished (or was force-unstuck): rewind off the sentinel and, if Play was
   *  clicked while we were still probing, honor that deferred intent now. */
  private resolveProbe(el: HTMLAudioElement): void {
    clearTimeout(this.probeTimer);
    this.probe = 'done';
    el.currentTime = 0;
    this.applyResume(el);
    if (this.pendingPlay) {
      this.pendingPlay = false;
      this.doPlay(el);
    }
  }

  onTime(ev: Event): void {
    const t = (ev.target as HTMLAudioElement).currentTime;
    // Gate on the sentinel value itself, not just the `probe` flag — if probe ever
    // gets stuck (see onMeta's safety timeout) this keeps sync alive regardless.
    if (t < TranscriptPlayerComponent.PROBE_SENTINEL_SEC) this.playhead.set(t);
    this.zoomer.follow(this.scroller?.nativeElement, this.cursorPct());
  }

  onPlay(): void {
    this.playing.set(true);
    // timeupdate is ~4 Hz — too coarse for word karaoke; rAF smooths it while playing.
    const tick = () => {
      const el = this.audioEl?.nativeElement;
      if (!el || el.paused) return;
      if (el.currentTime < TranscriptPlayerComponent.PROBE_SENTINEL_SEC) this.playhead.set(el.currentTime);
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  onPause(): void {
    this.playing.set(false);
    cancelAnimationFrame(this.raf);
  }

  /** Set when Play is clicked while the duration probe still has the element parked at
   *  the sentinel position (see onMeta) — calling play() there can silently produce no
   *  audio. Flushed by onDuration/the probe-stuck timeout once the element is safe. */
  private pendingPlay = false;

  private doPlay(el: HTMLAudioElement): void {
    void el.play().catch((err: DOMException) => {
      console.warn('[transcript-player] play() failed', {
        name: err?.name, message: err?.message, readyState: el.readyState,
        networkState: el.networkState, mediaError: el.error, src: el.currentSrc, probe: this.probe,
      });
    });
  }

  // ── transport ──
  toggle(): void {
    const el = this.audioEl?.nativeElement;
    if (!el) return;
    if (this.probe === 'probing') {
      // Element is parked at the 1e101 sentinel while duration resolves — play() here
      // can silently no-op. Defer until onDuration (or the stuck-probe timeout) clears it.
      this.pendingPlay = !this.pendingPlay;
      return;
    }
    if (el.paused) this.doPlay(el);
    else el.pause();
  }

  skip(sec: number): void {
    this.seekTo(this.playhead() + sec);
  }

  /** Flip one side. Refused when it would leave nothing to hear, or when the side
   *  that would be left alone has no file of its own (older recordings). */
  toggleSide(side: keyof PlaybackSides | string): void {
    if (side !== 'human' && side !== 'agent') return;
    const next = { ...this.sides(), [side]: !this.sides()[side] };
    if (!this.canToggle(side)) return;
    this.sides.set(next);
    this.setMode(playbackModeFor(next));
  }

  canToggle(side: keyof PlaybackSides | string): boolean {
    if (side !== 'human' && side !== 'agent') return false;
    const next = { ...this.sides(), [side]: !this.sides()[side] };
    if (!next.human && !next.agent) return false;
    return audioSourceFor(this.rec, playbackModeFor(next)).length > 0;
  }

  // ── debug actions ──
  /** Play one finding's span, hearing only the human, only the AI, or both. */
  playFinding(f: Finding, mode: PlaybackMode = 'mixed'): void {
    const sides = { human: mode !== 'agent', agent: mode !== 'human' };
    if (audioSourceFor(this.rec, playbackModeFor(sides)).length) { this.sides.set(sides); this.setMode(playbackModeFor(sides)); }
    this.dbg.selected.set(f);
    this.spans.start(f.t0, Math.max(f.t1, f.t0 + 0.5), this.dbg.loopSpan());
  }
  selectFinding(f: Finding): void { this.dbg.selected.set(this.dbg.selected() === f ? null : f); this.seekTo(f.t0, false); }
  copyReport(only?: Finding): void { void navigator.clipboard?.writeText(this.dbg.report(this.sessionId, only)); }

  /** Swap the driving file without losing the position. */
  private setMode(m: PlaybackMode): void {
    if (m === this.mode()) return;
    const el = this.audioEl?.nativeElement;
    this.resumeAt = this.playhead();
    this.resumePlay = !!el && !el.paused;
    clearTimeout(this.probeTimer);
    this.probe = 'idle';
    this.pendingPlay = false;
    this.mode.set(m);
    this.srcCandidates.set(audioSourceFor(this.rec, m));
    this.srcIdx.set(0);
  }

  setRate(r: number): void {
    this.rate.set(r);
    const el = this.audioEl?.nativeElement;
    if (el) el.playbackRate = r;
  }

  seekRow(row: TimedTurn): void {
    if (row.start == null) return;
    this.seekTo(row.start, true);
  }

  seekLane(ev: MouseEvent): void {
    const box = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (ev.clientX - box.left) / box.width));
    this.seekTo(pct * this.durationSec(), true);
  }

  private seekTo(sec: number, play = false): void {
    const el = this.audioEl?.nativeElement;
    if (!el) return;
    this.spans.stop();
    if (this.probe === 'probing') this.probe = 'done'; // viewer position beats the probe rewind
    el.currentTime = Math.min(Math.max(0, sec), this.durationSec() || sec);
    this.playhead.set(el.currentTime);
    if (play && el.paused) this.doPlay(el);
  }

  // ── display helpers ──
  readonly initials = initials;

  cursorPct(): number {
    const dur = this.durationSec();
    return dur ? Math.min(100, (this.playhead() / dur) * 100) : 0;
  }

  /** Turn card classes: each side keeps its own tint at all times; the non-active
   *  turns simply dim via opacity so the active one reads at a glance. */
  rowClass(row: TimedTurn, idx: number): string {
    const dimmed = this.activeIdx() !== -1 && this.activeIdx() !== idx;
    const side = row.ai
      ? 'border-r-2 border-emerald-400/70'
      : 'border-l-2 border-sky-400/70';
    const bg = row.ai ? 'bg-emerald-50/40 dark:bg-emerald-500/5' : 'bg-sky-50/40 dark:bg-sky-500/5';
    const opacity = dimmed ? 'opacity-50' : 'opacity-100';
    const cursor = row.start !== null ? 'cursor-pointer' : '';
    return `${side} ${bg} ${opacity} ${cursor}`;
  }

  laneBarClass(lane: TimelineLane): string { return laneBarClass(lane, this.humanLanes()); }
  laneDotClass(lane: TimelineLane): string { return laneDotClass(lane, this.humanLanes()); }
  readonly segTitle = segTitle;

  /** A row whose audio is not in the current Source is shown, but dimmed. */
  rowMuted(ai: boolean): boolean {
    const m = this.mode();
    return m !== 'mixed' && (m === 'agent') !== ai;
  }
}
